// ============================================================================
// Domain
// ============================================================================

/**
 * Registrable parent domain the SSO cookies are scoped to, shared by all
 * Aibuild products (OS, CAM) on that domain. Configurable per-deploy via
 * `VITE_COOKIE_DOMAIN` because GovCloud serves from `.ai-build-gov.com`, and a
 * cookie whose `Domain` isn't a suffix of the current host is silently dropped
 * by the browser — which logs the user out on refresh.
 */
export const AIBUILD_DOMAIN: string =
  import.meta.env.VITE_COOKIE_DOMAIN || '.ai-build.com';

/**
 * Whether the current hostname belongs to Aibuild OS
 * (e.g. os.ai-build.com, os-test.ai-build.com, or localhost).
 */
export const IS_OS_URL = (() => {
  if (globalThis.window === undefined) return false;
  const host = globalThis.location.hostname;
  if (host === 'localhost') return true;
  const firstLabel = host.split('.')[0];
  return firstLabel === 'os' || firstLabel.startsWith('os-');
})();

/**
 * Environment-specific cookie name suffix so that test / staging / prod
 * each get isolated SSO cookies on the shared `.ai-build.com` domain.
 *
 * Prod (`VITE_ENV=production` or unset) uses an empty string, keeping
 * the cookie names unchanged for backward compatibility. Non-prod builds
 * append `_<env>` (e.g. `_test`).
 *
 * Set via `VITE_COOKIE_SUFFIX` (takes precedence) or derived from `VITE_ENV`.
 */
export const COOKIE_SUFFIX: string = (() => {
  const explicit = import.meta.env.VITE_COOKIE_SUFFIX;
  if (explicit !== undefined && explicit !== '') return explicit;

  const env = import.meta.env.VITE_ENV;
  if (!env || env === 'production') return '';
  return `_${env}`;
})();

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  USER: 'aibuild_user',
  PENDING_INVITE_TOKEN: 'pendingInviteToken',
  THEME_PREFERENCE: 'aibuild_theme_preference',
  PENDING_CHAT_INPUT: 'pendingChatInput',
  FIRST_UTM: 'aibuild_first_utm',
  SESSION_UTM: 'aibuild_session_utm',
  SIDEBAR_COLLAPSED: 'aibuild_sidebar_collapsed',
} as const;

// ============================================================================
// Roles & Statuses
// ============================================================================

export const ORG_ROLES = {
  AIBUILD_ADMIN: 'AIBUILD_ADMIN',
  ADMIN: 'ORG_ADMIN',
  MEMBER: 'MEMBER',
} as const;

export const ORG_ROLE_LABELS: Record<string, string> = {
  AIBUILD_ADMIN: 'Aibuild Admin',
  ORG_ADMIN: 'Admin',
  MEMBER: 'Member',
} as const;

// ============================================================================
// File Upload
// ============================================================================

/** Maximum file size allowed for attachment uploads (500 MB — must match the backend limit). */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

export const BILLING_STATUSES = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;

// ============================================================================
// External URLs
// ============================================================================

export const JIRA_SUPPORT_DESK_URL =
  'https://ai-build-team.atlassian.net/servicedesk/customer/portal/2';

// EULA PDF for in-app display. The per-env `eulaUrl` from the API isn't used
// for display because GovCloud configures it as a same-origin path that the
// SPA fallback resolves to index.html (rendering the whole app in the iframe).
// GovCloud sets VITE_EULA_URL to a self-hosted same-origin path (kept inside
// the ITAR boundary); commercial defaults to the public asset bucket.
export const EULA_URL: string =
  import.meta.env.VITE_EULA_URL ||
  'https://aibuild-public-assets-bucket.s3.eu-west-1.amazonaws.com/eula.pdf';
